import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Persists review-mode state in the URL so a reload restores it. Shared by the
 * admin library (grid + PhotoReviewModal) and the client portal review
 * carousel — both use the same `reviewItem` param to mean "the feed is open on
 * this item", so grid <-> feed is a plain URL change.
 *
 * The `reviewItem` query param encodes both "review mode is open" (presence)
 * and "which item" (its value, a media id, or the `start` sentinel for the
 * beginning of the list). The initial id is captured once when the feed opens
 * and is NOT updated as the user navigates — otherwise every navigation would
 * re-seed the queue and re-fetch. Navigation only rewrites the param (via
 * `setCurrentItem`) so the position survives a reload.
 */
const PARAM = "reviewItem";
const START = "start";

export function useReviewUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get(PARAM);
  const isOpen = param !== null;

  // Captured once from the URL on mount (reload restores position). Never
  // recomputed from the live param, so navigation can't re-trigger the fetch.
  const [initialMediaId, setInitialMediaId] = useState<number | undefined>(() =>
    param && /^\d+$/.test(param) ? Number(param) : undefined
  );

  const writeParam = useCallback(
    (value: string | null) => {
      setSearchParams(
        (prev) => {
          // Skip redundant writes: the feed reports the current item on every
          // render, so most calls repeat the value already in the URL.
          if ((prev.get(PARAM) ?? null) === value) {
            return prev;
          }
          const next = new URLSearchParams(prev);
          if (value === null) {
            next.delete(PARAM);
          } else {
            next.set(PARAM, value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const open = useCallback(
    (mediaId?: number) => {
      setInitialMediaId(mediaId);
      writeParam(mediaId != null ? String(mediaId) : START);
    },
    [writeParam]
  );

  const close = useCallback(() => {
    setInitialMediaId(undefined);
    writeParam(null);
  }, [writeParam]);

  const setCurrentItem = useCallback(
    (mediaId: number) => {
      writeParam(String(mediaId));
    },
    [writeParam]
  );

  return { isOpen, initialMediaId, open, close, setCurrentItem };
}
